// ContactInfoStep Component
// Step 2: Contact information and location

import React, { useState } from 'react';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaRoad,
  FaCity,
  FaMapSigns,
  FaGlobe,
  FaBuilding
} from 'react-icons/fa';
import LocationPicker from '../../LocationPicker/LocationPicker';
import styles from './ContactInfoStep.module.css';

const ContactInfoStep = ({ formData, updateFormData, onNext, onBack }) => {
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const handleChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handleLocationSelect = (locationData) => {
    updateFormData({
      street: locationData.street || formData.street,
      city: locationData.city || formData.city,
      state: locationData.state || formData.state,
      country: locationData.country || formData.country,
      zipCode: locationData.zipCode || formData.zipCode,
      location: locationData.coordinates
    });
    setShowLocationPicker(false);
  };

  const isFormValid = () => {
    const hasContact = formData.contactNumber.trim() !== '';
    const hasCity = formData.city.trim() !== '';
    
    if (formData.clubType === 'Physical Location') {
      return (
        hasContact && 
        formData.street.trim() !== '' &&
        hasCity && 
        formData.state.trim() !== '' &&
        formData.zipCode.trim() !== ''
      );
    }
    return hasContact && hasCity;
  };

  const getLocationDisplayText = () => {
    const parts = [formData.city, formData.state, formData.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'No location selected';
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Contact Information</h2>
        <p className={styles.subtitle}>How can members reach your club?</p>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Club Type Toggle */}
        <div className={styles.clubTypeSection}>
          <label className={styles.label}>
            <FaBuilding className={styles.labelIcon} />
            Club Type
          </label>
          <div className={styles.clubTypeOptions}>
            <button
              type="button"
              className={`${styles.clubTypeOption} ${formData.clubType === 'Physical Location' ? styles.selected : ''}`}
              onClick={() => handleChange('clubType', 'Physical Location')}
            >
              <FaMapMarkerAlt />
              <span>Physical Location</span>
            </button>
            <button
              type="button"
              className={`${styles.clubTypeOption} ${formData.clubType === 'Virtual Club' ? styles.selected : ''}`}
              onClick={() => handleChange('clubType', 'Virtual Club')}
            >
              <FaGlobe />
              <span>Virtual Club</span>
            </button>
          </div>
        </div>

        {/* Contact Details */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaPhone className={styles.labelIcon} />
              Contact Number <span className={styles.required}>*</span>
            </label>
            <input
              type="tel"
              className={styles.input}
              value={formData.contactNumber}
              onChange={(e) => handleChange('contactNumber', e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaEnvelope className={styles.labelIcon} />
              Email Address
            </label>
            <input
              type="email"
              className={styles.input}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contact@yourclub.com"
            />
          </div>
        </div>

        {/* Location Section */}
        <div className={styles.locationSection}>
          <div className={styles.locationHeader}>
            <label className={styles.label}>
              <FaMapMarkerAlt className={styles.labelIcon} />
              Location
            </label>
            {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
              <button
                type="button"
                className={styles.pickLocationButton}
                onClick={() => setShowLocationPicker(true)}
              >
                <FaMapMarkerAlt />
                {formData.location ? 'Change on Map' : 'Pick on Map'}
              </button>
            )}
          </div>

          {formData.location && (
            <div className={styles.selectedLocation}>
              <FaMapMarkerAlt className={styles.locationIcon} />
              <span>{getLocationDisplayText()}</span>
            </div>
          )}

          {formData.clubType === 'Physical Location' && (
            <>
              {/* Street */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaRoad className={styles.labelIcon} />
                  Street Address <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                  placeholder="123 Main Street"
                />
              </div>

              {/* City, State, Zip */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FaCity className={styles.labelIcon} />
                    City <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="San Diego"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FaMapSigns className={styles.labelIcon} />
                    State <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="California"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FaEnvelope className={styles.labelIcon} />
                    Zip Code <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    value={formData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                    placeholder="92101"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FaGlobe className={styles.labelIcon} />
                    Country
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    placeholder="United States"
                  />
                </div>
              </div>
            </>
          )}

          {formData.clubType === 'Virtual Club' && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaCity className={styles.labelIcon} />
                  City <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Your city"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaGlobe className={styles.labelIcon} />
                  Country
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="Your country"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <div className={styles.modalOverlay} onClick={() => setShowLocationPicker(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                <FaMapMarkerAlt />
                Pick Location
              </h3>
              <button
                type="button"
                className={styles.modalCloseButton}
                onClick={() => setShowLocationPicker(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <LocationPicker
                value={{ 
                  coordinates: formData.location,
                  city: formData.city,
                  state: formData.state,
                  country: formData.country
                }}
                onLocationSelect={handleLocationSelect}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        <button className={styles.backButton} onClick={onBack}>
          Back
        </button>
        <button
          className={styles.nextButton}
          onClick={onNext}
          disabled={!isFormValid()}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ContactInfoStep;

